import React, { MutableRefObject, ReactElement, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { cmeDetailsInfoURL } from '../../../NonReact/constants/ApiConstants'
import { Estate as EstateLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { requestGetJSON, RESTAPIError } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { ReactState, StateSetter } from '../../Common/Types/Types'
import { EstateData } from '../Types'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

type Props = {
    setIsDataLoading: StateSetter<boolean>
    errorHandling: (e: RESTAPIError) => void
    cmeID?: string
}

/**
 * Estate content for the CME tabs.
 */
const Estate: React.FC<Props> = ({ cmeID, setIsDataLoading, errorHandling }: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()

    language.loadBundle('Estate')

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [tableData, setTableData]: ReactState<EstateData> = useState<EstateData>({
        ChildCMEnvironmentsCount: 0,
        TelSyssCount: 0,
        PartitionsCount: 0,
        DevicesCount: 0,
        ACDAgentsCount: 0,
        ReportingUsersCount: 0,
        NonReportingUsersCount: 0
    })

    useEffect(() => {
        if (cmeID) {
            setIsDataLoading(true)

            // This request is to retrieve the data for the state tab. It should
            // respond with a list of numbers referring to the count of everything
            // within the selected CME.
            requestGetJSON<EstateData>(cmeDetailsInfoURL(cmeID))
                .then((response: JSONResponse<EstateData>) => {
                    if (isMounted.current && response) {
                        setTableData(response)
                        setIsDataLoading(false)
                    }
                })
                .catch((e: RESTAPIError) => errorHandling(e))
        }
    }, [])

    const content = [
        {
            title: language.get(EstateLanguage.CMEnvironment),
            value: tableData.ChildCMEnvironmentsCount.toString()
        },
        {
            title: language.get(EstateLanguage.TelephonyServer),
            value: tableData.TelSyssCount.toString()
        },
        {
            title: language.get(EstateLanguage.Partitions),
            value: tableData.PartitionsCount.toString()
        },
        {
            title: language.get(EstateLanguage.Devices),
            value: tableData.DevicesCount.toString()
        },
        {
            title: language.get(EstateLanguage.Agents),
            value: tableData.ACDAgentsCount.toString()
        },
        {
            title: language.get(EstateLanguage.ReportingUsers),
            value: tableData.ReportingUsersCount.toString()
        },
        {
            title: language.get(EstateLanguage.NonReportingUsers),
            value: tableData.NonReportingUsersCount.toString()
        }
    ]

    const row: (label: string, value: string) => ReactElement = (label: string, value: string) => (
        <div className='estate-row' key={value + label}>
            <div className='estate-cell'>{label}</div>
            <div className='estate-cell'>{value}</div>
        </div>
    )

    return (
        <div className='estate-profile-tab tab-area'>
            <div className='estate-table'>
                <div className='estate-row'>
                    <div className='estate-header-cell'>{language.get(EstateLanguage.Item)}</div>
                    <div className='estate-header-cell'>{language.get(EstateLanguage.Quantity)}</div>
                </div>
                {content.map((data) => row(data.title, data.value))}
            </div>
        </div>
    )
}

export default Estate
